import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { RevealGroup, RevealItem } from "./Reveal";
import { SERVICES } from "../content/services";

/**
 * HomeServices — compact "What we build" grid on the home page.
 *
 * Sits between the proof strip and the Dynamic Output canvas so a
 * visitor (and a crawler) sees the full service catalog as plain,
 * linked HTML without having to prompt the assistant. Each tile is a
 * single link to /services/:slug. Data comes from content/services.ts,
 * shared with /services and the nav MegaMenu.
 *
 * Layout: 4 cols ≥1024px, 2 cols ≥640px, 1 col below.
 */
const CSS = `
  .nx-hs-tile {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 22px;
    border-radius: var(--nx-r-3, 14px);
    border: 1px solid var(--nx-line);
    background: linear-gradient(180deg, rgba(255,255,255,0.018), rgba(255,255,255,0) 60%), var(--nx-surface);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
    transition: border-color .25s ease, transform .25s ease, box-shadow .25s ease;
  }
  .nx-hs-tile:hover {
    border-color: color-mix(in srgb, var(--nx-accent) 45%, var(--nx-line));
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -12px color-mix(in srgb, var(--nx-accent) 35%, transparent);
  }
  .nx-hs-tile:focus-visible {
    outline: 2px solid var(--nx-accent);
    outline-offset: 3px;
  }
  .nx-hs-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 1px solid var(--nx-line);
    background:
      radial-gradient(120% 120% at 30% 0%, color-mix(in srgb, var(--nx-accent) 28%, transparent), transparent 70%),
      var(--nx-surface-2);
    color: #C4BEFF;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.07), 0 0 0 4px rgba(107,79,230,0.04);
    transition: color .25s ease, border-color .25s ease;
  }
  .nx-hs-tile:hover .nx-hs-icon { color: #fff; border-color: color-mix(in srgb, var(--nx-accent) 50%, var(--nx-line)); }
  .nx-hs-arrow { transition: transform .2s ease, color .2s ease; }
  .nx-hs-tile:hover .nx-hs-arrow { transform: translate(2px,-2px); color: var(--nx-text); }
  @media (prefers-reduced-motion: reduce) {
    .nx-hs-tile, .nx-hs-arrow, .nx-hs-icon { transition: none; }
    .nx-hs-tile:hover { transform: none; }
  }
`;

export function HomeServices() {
  return (
    <section
      className="border-b border-nx-line/60"
      aria-labelledby="home-services-h"
      data-home-services
    >
      <style>{CSS}</style>
      <div className="nx-container px-7 py-20 md:py-24">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <span className="nx-eyebrow">What we build</span>
            <h2
              id="home-services-h"
              className="mt-5 font-display text-[clamp(1.9rem,3.6vw,2.9rem)] font-semibold leading-[1.05] tracking-tight text-nx-text"
            >
              Custom software and AI agents,{" "}
              <span className="text-nx-text-dim">engineered to run your business.</span>
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex shrink-0 items-center gap-1.5 self-start text-sm text-nx-text-dim transition-colors hover:text-nx-text md:self-auto"
          >
            All services <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        <RevealGroup className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <RevealItem key={s.slug}>
              <Link to={`/services/${s.slug}`} className="nx-hs-tile group" data-home-service={s.slug}>
                <div className="flex items-start justify-between">
                  <span className="nx-hs-icon" aria-hidden>
                    <s.icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <ArrowUpRight className="nx-hs-arrow h-4 w-4 text-nx-text-faint" aria-hidden />
                </div>
                <h3 className="mt-5 font-display text-[17px] font-semibold tracking-tight text-nx-text">
                  {s.name}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-nx-text-dim">{s.line}</p>
              </Link>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
}

export default HomeServices;
